// FLAGS: identity
// Google auth-code sign-in and sign-up for staff accounts; both paths demand the Google Calendar scope
// because the expert's agenda is read from it, and both hand the code to the API to exchange.
import { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import {
  useGoogleLogin,
  type UseGoogleLoginOptionsAuthCodeFlow,
} from "@react-oauth/google";
import { AxiosError } from "axios";
import Button from "@openup/shared/components/Button/Button";
import GoogleLogo from "@src/components/images/google-logo.svg";
import { withSnackbar } from "@src/components/SnackBarComponent";
import { timeSnackbar } from "@src/constants";
import { USER_LOCKED_OUT_ERROR } from "@src/utils/constants";
import LockedAccountMessage from "@src/components/LockedAccountMessage";
import authenticationService from "@src/services/authentication.service";
import LoginContainer from "./LoginContainer";

type OnSuccessHandler = NonNullable<
  UseGoogleLoginOptionsAuthCodeFlow["onSuccess"]
>;
type OnErrorHandler = NonNullable<UseGoogleLoginOptionsAuthCodeFlow["onError"]>;

interface WelcomePageProps {
  moveToNextStep: () => void;
  snackbarShowMessage: (
    message: string | ReactElement,
    severity?: string,
    duration?: number,
  ) => void;
}

const WelcomePage = ({
  moveToNextStep,
  snackbarShowMessage,
}: WelcomePageProps) => {
  const { t } = useTranslation();
  const calendarScope = "https://www.googleapis.com/auth/calendar";

  const onFailure = (errorResponse: Parameters<OnErrorHandler>[0]) => {
    snackbarShowMessage(errorResponse.error || "", "error", 10000);
  };

  const onSuccessLogin = async (result: Parameters<OnSuccessHandler>[0]) => {
    try {
      await authenticationService.loginExternalUser(result.code, "Google");
      snackbarShowMessage("", "success");
      setTimeout(() => {
        moveToNextStep();
      }, timeSnackbar);
    } catch (error) {
      const axiosError = error as AxiosError<string>;
      snackbarShowMessage(
        axiosError.response?.data === USER_LOCKED_OUT_ERROR ? (
          <LockedAccountMessage />
        ) : (
          axiosError.response?.data || axiosError.message || ""
        ),
        "error",
        5000,
      );
    }
  };

  const onSuccessSignUp = async (result: Parameters<OnSuccessHandler>[0]) => {
    try {
      await authenticationService.registerExternalUser(result.code, "Google");
      snackbarShowMessage("", "success");
      setTimeout(() => {
        moveToNextStep();
      }, timeSnackbar);
    } catch (error) {
      const axiosError = error as AxiosError<string>;
      snackbarShowMessage(
        axiosError.response?.data || axiosError.message || "",
        "error",
      );
    }
  };

  const checkScopeOnLogin = async (result: Parameters<OnSuccessHandler>[0]) => {
    const hasAccess = result.scope.includes(calendarScope);
    if (hasAccess) {
      await onSuccessLogin(result);
    } else {
      snackbarShowMessage(t("WelcomePage.Error"), "error", 600000);
    }
  };

  const checkScopeOnSignUp = async (
    result: Parameters<OnSuccessHandler>[0],
  ) => {
    const hasAccess = result.scope.includes(calendarScope);
    if (hasAccess) {
      await onSuccessSignUp(result);
    } else {
      snackbarShowMessage(t("WelcomePage.Error"), "error", 600000);
    }
  };

  const logInPsychologist = useGoogleLogin({
    onSuccess: (response) => {
      checkScopeOnLogin(response);
    },
    scope: calendarScope,
    flow: "auth-code",
    onError: (error) => {
      onFailure(error);
    },
  });

  const signUpPsychologist = useGoogleLogin({
    onSuccess: (response) => {
      checkScopeOnSignUp(response);
    },
    scope: calendarScope,
    flow: "auth-code",
    onError: (error) => {
      onFailure(error);
    },
  });

  return (
    <LoginContainer title={t("LoginView.Welcome")} variant="h1">
      <div className="flex flex-col h-full justify-center items-center text-center mt-6">
        <div className="mb-6">
          <p className="text-base">{t("LoginView.WelcomePage.Text")}</p>
        </div>
        <Button
          variant="tertiary"
          size="large"
          className="w-full max-w-[360px] gap-2 outline-0 border-2 border-solid border-indigo-800 bg-white hover:bg-white hover:opacity-100"
          onClick={() => logInPsychologist()}
        >
          <span className="flex items-center overflow-visible w-6 h-6">
            <GoogleLogo />
          </span>
          <span className="text-sm font-medium tracking-widest">
            {t("LoginView.WelcomePage.LoginGoogle")}
          </span>
        </Button>

        <div className="mt-16 mb-1">
          <p className="text-base">{t("LoginView.WelcomePage.NewAtOpenUp")}</p>
        </div>

        <Button
          variant="tertiary"
          size="large"
          className="w-full max-w-[360px] gap-2 outline-0 border-2 border-solid border-indigo-800 bg-white hover:bg-white hover:opacity-100"
          onClick={() => signUpPsychologist()}
        >
          <span className="flex items-center overflow-visible w-6 h-6">
            <GoogleLogo />
          </span>
          <span className="text-sm font-medium tracking-widest">
            {t("LoginView.WelcomePage.GoogleRegister")}
          </span>
        </Button>
      </div>
    </LoginContainer>
  );
};

export default withSnackbar(WelcomePage);
